import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { ApiError, createApiClient } from '../../services/api/client'
import {
  notificationRecipientDeletionResponseSchema,
  notificationSettingsPayloadSchema,
  notificationSettingsResponseSchema,
  type NotificationRecipientDeletionResponse,
  type NotificationSettingsPayload,
  type NotificationSettingsResponse,
} from './notification-settings-contracts'

export type NotificationSettingsRepository = {
  getSettings: (tenantId: string) => Promise<NotificationSettingsResponse>
  updateSettings: (
    tenantId: string,
    payload: NotificationSettingsPayload
  ) => Promise<NotificationSettingsResponse>
  deleteRecipient: (
    tenantId: string,
    recipientId: string
  ) => Promise<NotificationRecipientDeletionResponse>
}

type RepositoryOptions = {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}

function endpoint(tenantId: string) {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/notification-settings`
}

function recipientEndpoint(tenantId: string, recipientId: string) {
  return `${endpoint(tenantId)}/${encodeURIComponent(recipientId)}`
}

export function createNotificationSettingsRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: RepositoryOptions): NotificationSettingsRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })

  return {
    getSettings(tenantId) {
      return client.request(endpoint(tenantId), {
        method: 'GET',
        responseSchema: notificationSettingsResponseSchema,
      })
    },
    updateSettings(tenantId, payload) {
      return client.request(endpoint(tenantId), {
        method: 'PATCH',
        body: notificationSettingsPayloadSchema.parse(payload),
        responseSchema: notificationSettingsResponseSchema,
      })
    },
    deleteRecipient(tenantId, recipientId) {
      return client.request(recipientEndpoint(tenantId, recipientId), {
        method: 'DELETE',
        responseSchema: notificationRecipientDeletionResponseSchema,
      })
    },
  }
}

export function isNotificationSettingsPermissionError(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.status === 401 ||
      error.status === 403 ||
      error.code === 'unauthenticated')
  )
}
